import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import LineIcon from '../components/LineIcon';
import { plural } from '../garage/format';
import { useEvaluations } from './ProviderContext';
import EvaluationRow from './EvaluationRow';
import EvaluationSheet from './EvaluationSheet';
import { filterEvaluations } from './providerData';

const PAGE_SIZE = 20;

const INDICATE = [
  { value: 'all', label: 'Todas' },
  { value: 'yes', label: 'Indicariam' },
  { value: 'no', label: 'Não indicariam' },
];

// Lista de avaliacoes com busca por cliente ou placa; filtro so aparece quando filtra algo.
export default function EvaluationsPage() {
  const location = useLocation();
  const { loading, list, error, reload } = useEvaluations();
  const [query, setQuery] = useState('');
  const [indicate, setIndicate] = useState(() => {
    const value = new URLSearchParams(location.search).get('indica');
    return INDICATE.some((option) => option.value === value) ? value : 'all';
  });
  const [type, setType] = useState('all');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [open, setOpen] = useState(null);

  const types = useMemo(() => [...new Set(list.map((item) => item.type).filter(Boolean))].sort(), [list]);
  const filtered = useMemo(() => filterEvaluations(list, { query, indicate, type }), [list, query, indicate, type]);
  const hasNegative = list.some((item) => !item.indicate);

  const change = (setter) => (value) => {
    setter(value);
    setVisible(PAGE_SIZE);
  };

  return (
    <div className='gs-stack'>
      <header className='gs-page-header'>
        <span className='gs-eyebrow'>Avaliações</span>
        <h1>O que os clientes disseram</h1>
        {!loading && !error && list.length > 0 && (
          <p>{list.length} {plural(list.length, 'avaliação recebida', 'avaliações recebidas')}.</p>
        )}
      </header>

      {loading && <div className='gs-skeleton' aria-busy='true' aria-label='Carregando avaliações' />}

      {!loading && error && (
        <p className='gs-notice gs-notice-error' role='alert'>
          Não foi possível carregar as avaliações.
          <button type='button' className='gs-link' onClick={reload}>Tentar de novo</button>
        </p>
      )}

      {!loading && !error && list.length === 0 && (
        <section className='gs-empty'>
          <span className='gs-icon gs-icon-orange' aria-hidden='true'><LineIcon name='star' /></span>
          <h2>Ainda sem avaliações</h2>
          <p className='gs-muted'>As respostas da pesquisa de satisfação aparecem aqui.</p>
        </section>
      )}

      {!loading && !error && list.length > 0 && (
        <>
          <div className='pv-filters'>
            <label className='pv-search'>
              <span className='pv-sr-only'>Buscar por cliente ou placa</span>
              <input
                type='search'
                value={query}
                placeholder='Buscar por cliente ou placa'
                onChange={(event) => change(setQuery)(event.target.value)}
              />
            </label>
            {(hasNegative || types.length > 1) && (
              <div className='gs-chips-row' role='group' aria-label='Filtros'>
                {hasNegative && INDICATE.map((option) => (
                  <button
                    key={option.value}
                    type='button'
                    className={`gs-filter-chip${indicate === option.value ? ' is-active' : ''}`}
                    aria-pressed={indicate === option.value}
                    onClick={() => change(setIndicate)(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
                {types.length > 1 && types.map((option) => (
                  <button
                    key={option}
                    type='button'
                    className={`gs-filter-chip${type === option ? ' is-active' : ''}`}
                    aria-pressed={type === option}
                    onClick={() => change(setType)(type === option ? 'all' : option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>

          {filtered.length === 0
            ? <p className='gs-muted pv-none'>Nenhuma avaliação encontrada com esses filtros.</p>
            : (
              <ul className='pv-list'>
                {filtered.slice(0, visible).map((evaluation) => (
                  <EvaluationRow key={evaluation.key} evaluation={evaluation} onOpen={setOpen} />
                ))}
              </ul>
            )}

          {filtered.length > visible && (
            <button type='button' className='gs-btn gs-btn-secondary pv-more-btn' onClick={() => setVisible((count) => count + PAGE_SIZE)}>
              Mostrar mais
            </button>
          )}
        </>
      )}

      {open && <EvaluationSheet evaluation={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
